import { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", lat: null, lng: null, maps_url: "", towers: "", flats: 0, notes: "", active: true };

const MapsFetch = ({ f, setF }) => {
  const [link, setLink] = useState(f.maps_url || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setLink(f.maps_url || ""), [f.maps_url]);
  const fetchPlace = async () => {
    setBusy(true);
    try { const { data } = await api.post("/societies/resolve", { link }); setF((x) => ({ ...x, name: data.name || x.name, lat: data.lat, lng: data.lng, maps_url: data.maps_url })); toast.success(data.name ? `Found: ${data.name}` : "Location found. Type the society name"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="col-span-2 space-y-1">
      <Label>Google Maps link of the society gate</Label>
      <div className="flex gap-2">
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://maps.app.goo.gl/..." data-testid="society-maps-input" />
        <Button type="button" variant="outline" disabled={busy || !link.trim()} onClick={fetchPlace} data-testid="society-maps-fetch">{busy ? "Fetching..." : "Fetch"}</Button>
      </div>
      <div className={`text-xs ${f.lat != null ? "text-emerald-700" : "text-muted-foreground"}`} data-testid="society-location-status">{f.lat != null ? "Location pinned for route planning" : "Open the society in Google Maps, tap Share, copy link and paste it here"}</div>
    </div>
  );
};

function SocietyDialog({ value, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (value) setF({ ...EMPTY, ...value, towers: (value.towers || []).join(", ") }); }, [value]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, flats: +f.flats || 0,
      towers: f.towers.split(",").map((t) => t.trim()).filter(Boolean) };
    try { await (value.id ? api.put(`/societies/${value.id}`, body) : api.post("/societies", body)); toast.success("Society saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="society-dialog">
        <DialogHeader><DialogTitle className="font-display">{value?.id ? "Edit society" : "Add society"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <MapsFetch f={f} setF={setF} />
          <div className="col-span-2 space-y-1"><Label>Society name</Label><Input value={f.name} onChange={set("name")} placeholder="Filled from Google Maps, edit if needed" data-testid="society-name-input" /></div>
          <div className="col-span-2 space-y-1"><Label>Towers / blocks (comma separated)</Label><Input value={f.towers} onChange={set("towers")} placeholder="A, B, C, D" data-testid="society-towers-input" /></div>
          <div className="space-y-1"><Label>Total flats</Label><Input type="number" value={f.flats} onChange={set("flats")} data-testid="society-flats-input" /></div>
          <div className="space-y-1"><Label>Gate / security notes</Label><Input value={f.notes} onChange={set("notes")} data-testid="society-notes-input" /></div>
        </div>
        <DialogFooter><Button disabled={!f.name.trim()} onClick={save} className="bg-[#0F382C] hover:bg-[#134E3A]" data-testid="society-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Societies() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/societies").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const match = async () => {
    try { const r = await api.post("/societies/auto-match"); toast.success(`${r.data.societies_matched} customers matched to societies, ${r.data.units_filled} tower/flat filled`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="societies-page">
      <PageHeader eyebrow="Gated communities" title="Societies" subtitle="Deliveries are planned society by society and tower by tower, with at most the set number of drops per partner.">
        <Button variant="outline" className="rounded-full" onClick={match} data-testid="auto-match-btn"><Wand2 className="mr-1 h-4 w-4" />Auto-match customers</Button>
        <Button className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => setEdit({})} data-testid="add-society-btn"><Plus className="mr-1 h-4 w-4" />Add society</Button>
      </PageHeader>
      <div className="rounded-2xl border border-[#E7E2D6] bg-white p-2 sm:p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Society</TableHead><TableHead>Towers</TableHead><TableHead className="text-right">Flats</TableHead><TableHead className="text-right">Customers</TableHead><TableHead>Location</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground"><Building2 className="mx-auto mb-2 h-6 w-6" />Add your first society</TableCell></TableRow>}
            {rows.map((s) => (
              <TableRow key={s.id} data-testid={`society-row-${s.id}`}>
                <TableCell><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.notes}</div></TableCell>
                <TableCell className="text-sm">{(s.towers || []).join(", ") || "-"}</TableCell>
                <TableCell className="text-right font-mono">{s.flats || "-"}</TableCell>
                <TableCell className="text-right font-mono" data-testid={`society-customers-${s.id}`}>{s.customers}</TableCell>
                <TableCell className="text-xs">{s.lat != null ? <a href={s.maps_url || `https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="font-medium text-emerald-700 hover:underline" data-testid={`society-maps-${s.id}`}>Pinned · Open in Maps</a> : <span className="text-rose-600">Not set</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setEdit(s)} data-testid={`society-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => api.delete(`/societies/${s.id}`).then(load)} data-testid={`society-delete-${s.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <SocietyDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
